<script setup lang="ts">
import api from "@/api";
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { useDefaultStore } from "@/store";
import { useToast } from "primevue/usetoast";

;

const store = useDefaultStore();
const toast = useToast();
const router = useRouter();

type TFilterPreset = {
	code: string;
	description: string;
	label: string;
};

const username = ref("");
const initialUsername = ref("");
const showAllPeople = ref(false);
const initialShowAllPeople = ref(false);
const excludeAdultNames = ref(false);
const initialExcludeAdultNames = ref(false);
const filterPresets = ref<TFilterPreset[]>([]);
const selectedFilterPresetCode = ref("");
const initialFilterPresetCode = ref("");
const isLoading = ref(true);
const isSaving = ref(false);

function normalizeBoolean(raw: unknown): boolean {
	if (typeof raw === "boolean") {
		return raw;
	}

	if (typeof raw === "number") {
		return raw === 1;
	}

	if (typeof raw === "string") {
		return raw === "1" || raw === "true";
	}

	return false;
}

const hasChanges = computed(() => {
	return (
		username.value.trim() !== initialUsername.value.trim() ||
		showAllPeople.value !== initialShowAllPeople.value ||
		excludeAdultNames.value !== initialExcludeAdultNames.value ||
		selectedFilterPresetCode.value !== initialFilterPresetCode.value
	);
});
const filterPresetOptions = computed<TFilterPreset[]>(() => [
	{
		code: "",
		description: "Use the regular People Pool setting.",
		label: "No preset",
	},
	...filterPresets.value,
]);

function getFirstArray(...values: any[]): any[] {
	for (const value of values) {
		if (Array.isArray(value)) {
			return value;
		}
	}

	return [];
}

function normalizeFilterPreset(raw: any): TFilterPreset | null {
	const code = String(
		raw?.code ??
			raw?.preset_code ??
			raw?.filter_preset_code ??
			raw?.fpr_code ??
			raw?.value ??
			"",
	).trim();

	if (!code) {
		return null;
	}

	return {
		code,
		description: String(
			raw?.description ?? raw?.fpr_description ?? raw?.text ?? "",
		).trim(),
		label:
			String(
				raw?.label ?? raw?.name ?? raw?.title ?? raw?.fpr_name ?? code,
			).trim() || code,
	};
}

function getFilterPresetsFromResponse(payload: any) {
	const root = payload?.data?.data ?? payload?.data?.res ?? payload?.data ?? payload;

	return getFirstArray(
		root?.filter_presets,
		root?.filterPresets,
		root?.items,
		root?.data,
		root,
	)
		.map(normalizeFilterPreset)
		.filter((preset): preset is TFilterPreset => Boolean(preset));
}

function syncLocalStateFromStore() {
	const currentUsername = store.userData?.usr_username ?? "";
	const currentPeopleOnlyGloballyKnown = normalizeBoolean(
		store.userData?.usr_people_only_globally_known,
	);
	const currentShowAdultNames = normalizeBoolean(
		store.userData?.usr_people_show_adult_names ?? 1,
	);
	const currentFilterPresetCode =
		store.userData?.usr_people_filter_preset_code ?? "";

	if (
		currentFilterPresetCode &&
		!filterPresets.value.some((preset) => preset.code === currentFilterPresetCode)
	) {
		filterPresets.value = [
			{
				code: currentFilterPresetCode,
				description: "Currently selected preset.",
				label: currentFilterPresetCode,
			},
			...filterPresets.value,
		];
	}

	username.value = currentUsername;
	initialUsername.value = currentUsername;
	showAllPeople.value = !currentPeopleOnlyGloballyKnown;
	initialShowAllPeople.value = !currentPeopleOnlyGloballyKnown;
	excludeAdultNames.value = !currentShowAdultNames;
	initialExcludeAdultNames.value = !currentShowAdultNames;
	selectedFilterPresetCode.value = currentFilterPresetCode;
	initialFilterPresetCode.value = currentFilterPresetCode;
}

async function loadFilterPresets(token: string | null) {
	const res = await api.getFilterPresets({ token });
	filterPresets.value = getFilterPresetsFromResponse(res);
}

async function loadUser() {
	try {
		isLoading.value = true;

		if (!store.userData?.usr_token) {
			await store.getUser();
		}

		await loadFilterPresets(store.userData?.usr_token ?? store.playerToken ?? null);

		syncLocalStateFromStore();
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Unable to load user settings.",
			life: 3000,
		});
	} finally {
		isLoading.value = false;
	}
}

async function saveSettings() {
	const trimmedName = username.value.trim();
	const token = store.userData?.usr_token ?? "";

	if (!token) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Missing user token.",
			life: 3000,
		});
		return;
	}

	if (!trimmedName) {
		toast.add({
			severity: "warn",
			summary: "Validation",
			detail: "Username cannot be empty.",
			life: 2500,
		});
		return;
	}

	if (!hasChanges.value) {
		return;
	}

	try {
		isSaving.value = true;

		const res = await api.editUser({
			token,
			name: trimmedName,
			peopleFilterPresetCode: selectedFilterPresetCode.value || null,
			peopleOnlyGloballyKnown: showAllPeople.value ? 0 : 1,
			peopleShowAdultNames: excludeAdultNames.value ? 0 : 1,
		});

		const data = res.data.data;

		if (!data?.ok) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail: data?.showText
					? (data?.message ?? "Unable to save settings.")
					: "Unable to save settings.",
				life: 3500,
			});

			await router.push({
				name: "PeoplePage",
			});
			return;
		}

		await store.getUser();
		syncLocalStateFromStore();

		toast.add({
			severity: "success",
			summary: "Saved",
			detail: "Settings updated successfully.",
			life: 2500,
		});
	} catch (err: any) {
		console.warn(err.message);
		const showText = err?.response?.data?.data?.showText;
		const customErrorMsg = err?.response?.data?.data?.message;

		toast.add({
			severity: "error",
			summary: "Error",
			detail: showText
				? (customErrorMsg ?? "Unable to save settings.")
				: "Unable to save settings.",
			life: 3500,
		});

		await router.push({
			name: "PeoplePage",
		});
	} finally {
		isSaving.value = false;
	}
}

onMounted(() => {
	loadUser();
});

useHead({
	title: getPageTitle("People Settings"),
	meta: [
		{
			name: "title",
			content: getPageTitle("People Settings"),
		},
		{
			name: "description",
			content: `Manage your ${APP_NAME} people game settings.`,
		},
		{ name: "robots", content: "index, follow" },
		{
			property: "og:title",
			content: getPageTitle("People Settings"),
		},
		{
			property: "og:description",
			content: `Manage your ${APP_NAME} people game settings.`,
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>

<template>
	<section class="settings-page">
		<div class="overlay"></div>

		<div class="page-shell">
			<div class="settings-card">
				<RouterLink
					:to="{ name: 'PeoplePage' }"
					class="category-back-link"
				>
					← Back
				</RouterLink>

				<div class="header">
					<p class="eyebrow">Settings</p>
					<h1 class="title">People Settings</h1>
				</div>

				<div
					v-if="isLoading"
					class="state-box"
				>
					Loading settings...
				</div>

				<div
					v-else
					class="form-block"
				>
					<div class="field-group">
						<label
							for="username"
							class="field-label"
						>
							Username
						</label>

						<input
							id="username"
							v-model="username"
							type="text"
							class="field-input"
							maxlength="40"
							placeholder="Enter username"
							:disabled="isSaving"
						/>
					</div>

					<div class="field-group">
						<label class="field-label">Filter Preset</label>

						<Select
							v-model="selectedFilterPresetCode"
							:options="filterPresetOptions"
							optionLabel="label"
							optionValue="code"
							filter
							:filterFields="['label', 'description', 'code']"
							filterPlaceholder="Search presets"
							placeholder="Select filter preset"
							class="field-control"
							:pt="{
								overlay: { class: 'settings-overlay-panel' },
							}"
							:disabled="isSaving"
						>
							<template #option="{ option }">
								<div class="preset-select-option">
									<strong>{{ option.label }}</strong>
									<span v-if="option.description">
										{{ option.description }}
									</span>
								</div>
							</template>
						</Select>

						<p class="checkbox-note">
							Choosing a preset means the popularity filter will not apply.
						</p>
					</div>

					<div class="field-group">
						<label class="field-label">People Pool</label>

						<label class="checkbox-row">
							<input
								v-model="showAllPeople"
								type="checkbox"
								class="checkbox-input"
								:disabled="isSaving"
							/>

							<span class="checkbox-box"></span>
							<span class="checkbox-text">Show all</span>
						</label>

						<p class="checkbox-note">
							By checking this, all names will be shown regardless of
							popularity.
						</p>

						<label class="checkbox-row">
							<input
								v-model="excludeAdultNames"
								type="checkbox"
								class="checkbox-input"
								:disabled="isSaving"
							/>

							<span class="checkbox-box"></span>
							<span class="checkbox-text">Exclude adult names</span>
						</label>

						<p class="checkbox-note">
							By checking this, adult names will be excluded from People games.
						</p>
					</div>

					<button
						type="button"
						class="save-button"
						:disabled="isSaving || !hasChanges"
						@click="saveSettings"
					>
						{{ isSaving ? "Saving..." : "Save Changes" }}
					</button>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.field-group {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.checkbox-row {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	user-select: none;
	width: fit-content;
}

.checkbox-input {
	position: absolute;
	opacity: 0;
	pointer-events: none;
}

.checkbox-box {
	width: 20px;
	height: 20px;
	border-radius: 6px;
	border: 1px solid rgba(255, 255, 255, 0.18);
	background: rgba(255, 255, 255, 0.06);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		box-shadow 0.2s ease;
}

.checkbox-box::after {
	content: "";
	width: 10px;
	height: 10px;
	border-radius: 3px;
	background: rgba(255, 255, 255, 0.9);
	transform: scale(0);
	transition: transform 0.18s ease;
}

.checkbox-input:checked + .checkbox-box {
	background: rgba(255, 255, 255, 0.16);
	border-color: rgba(255, 255, 255, 0.24);
}

.checkbox-input:checked + .checkbox-box::after {
	transform: scale(1);
}

.checkbox-input:focus + .checkbox-box {
	box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.06);
}

.checkbox-text {
	font-size: 15px;
	font-weight: 600;
	color: $text-color-white;
}

.checkbox-note {
	margin: 0;
	font-size: 13px;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.62);
}

.field-control {
	width: 100%;
}

:deep(.field-control.p-select) {
	width: 100%;
	height: 50px;
	min-height: 50px;
	border-radius: 16px;
	border: 1px solid rgba(255, 255, 255, 0.14) !important;
	background: rgba(255, 255, 255, 0.08) !important;
	color: $text-color-white !important;
	backdrop-filter: blur(8px);
	box-shadow: none !important;
	transition:
		border-color 0.2s ease,
		background 0.2s ease,
		box-shadow 0.2s ease;
}

:deep(.field-control.p-select:not(.p-disabled):hover) {
	border-color: rgba(255, 255, 255, 0.2) !important;
	background: rgba(255, 255, 255, 0.1) !important;
}

:deep(.field-control.p-select.p-focus) {
	border-color: rgba(255, 255, 255, 0.28) !important;
	background: rgba(255, 255, 255, 0.1) !important;
	box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.06) !important;
}

:deep(.field-control.p-select .p-select-label) {
	display: flex;
	align-items: center;
	height: 100%;
	padding: 0 16px !important;
	background: transparent !important;
	color: $text-color-white !important;
	font-size: 15px;
}

:deep(.field-control.p-select .p-placeholder),
:deep(.field-control.p-select .p-select-label.p-placeholder) {
	color: rgba(255, 255, 255, 0.45) !important;
}

:deep(.field-control.p-select .p-select-dropdown) {
	width: 44px;
	min-width: 44px;
	background: transparent !important;
	border: 0 !important;
	box-shadow: none !important;
	color: rgba(255, 255, 255, 0.72) !important;
}

.preset-select-option {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 2px 0;
}

.preset-select-option strong {
	color: $text-color-white;
	font-size: 14px;
	font-weight: 800;
	line-height: 1.25;
}

.preset-select-option span {
	color: rgba(255, 255, 255, 0.62);
	font-size: 13px;
	line-height: 1.35;
}

:global(.settings-overlay-panel) {
	margin-top: 8px;
	border-radius: 18px;
	border: 1px solid rgba(255, 255, 255, 0.12);
	background: rgba(14, 18, 28, 0.94) !important;
	backdrop-filter: blur(14px);
	box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
	overflow: hidden;
}

:global(.settings-overlay-panel .p-select-header) {
	padding: 10px 12px;
	background: rgba(255, 255, 255, 0.04) !important;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

:global(.settings-overlay-panel .p-select-filter) {
	width: 100%;
	height: 42px;
	padding: 0 12px;
	border-radius: 12px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: rgba(255, 255, 255, 0.06) !important;
	color: $text-color-white !important;
	outline: none;
	box-shadow: none;
}

:global(.settings-overlay-panel .p-select-filter::placeholder) {
	color: rgba(255, 255, 255, 0.45);
}

:global(.settings-overlay-panel .p-select-list-container),
:global(.settings-overlay-panel .p-select-list) {
	background: transparent !important;
}

:global(.settings-overlay-panel .p-select-option) {
	color: rgba(255, 255, 255, 0.88) !important;
	background: transparent !important;
	transition:
		background 0.18s ease,
		color 0.18s ease;
}

:global(.settings-overlay-panel .p-select-option:hover) {
	background: rgba(255, 255, 255, 0.08) !important;
	color: $text-color-white !important;
}

:global(.settings-overlay-panel .p-select-option.p-select-option-selected) {
	background: rgba(255, 255, 255, 0.12) !important;
	color: $text-color-white !important;
}

:global(.settings-overlay-panel .p-select-empty-message) {
	padding: 14px 16px;
	color: rgba(255, 255, 255, 0.58) !important;
}
</style>
